import React, { useState } from "react";
import { DashboardShell } from "@/components/DashboardLayout";
import { BackButton } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt } from "@/components/Receipt";
import { api, fmtSASTDate, fmtZAR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Printer, ScanLine, Search } from "lucide-react";

const SERVICES = [
  { name: "general", label: "General" },
  { name: "xlearna", label: "XLearna Premium" },
  { name: "airtime", label: "Airtime" },
  { name: "data", label: "Mobile Data" },
  { name: "electricity", label: "Electricity" },
];

export default function MerchantRedeem() {
  const [pin, setPin] = useState("");
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [service, setService] = useState("general");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const enquire = async (e) => {
    e?.preventDefault();
    const cleanPin = pin.trim().replace(/[\s-]/g, "");
    if (!cleanPin) {
      toast.error("Enter the voucher PIN before searching.");
      return;
    }
    if (cleanPin.length < 12) {
      toast.error("Voucher PINs are 16 digits — please check what the customer entered.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/balance", { pin: pin.trim() });
      setInfo(data);
      setService(data.intended_service || "general");
      toast.success(`Voucher status: ${data.status.replace("_", " ")}`);
    } catch (err) {
      setInfo(null);
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const redeem = async () => {
    if (!info) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/redeem", {
        pin: pin.trim(),
        amount: parseFloat(amount),
        service,
      });
      setReceipt(data.receipt);
      setInfo({ ...info, balance: data.remaining_balance, status: data.status });
      setAmount("");
      toast.success(`Redeemed ${fmtZAR(data.redeemed_amount)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardShell role="merchant">
      <BackButton to="/dashboard" />
      <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#00C4B4]">Voucher redemption</p>
      <h1 className="font-display mt-2 text-3xl font-bold text-[#0A2540]">Redeem a voucher</h1>
      <p className="mt-3 text-sm text-[#666666] max-w-2xl leading-relaxed">
        A customer hands you their 16-digit FlexiVouch PIN at the counter (in store, over the phone, or via SMS). Enter the PIN below to look up its remaining balance, then redeem the amount they want to spend on airtime, data, electricity, XLearna Premium, or general value. Partial redemption is fully supported — anything left on the voucher stays available for future purchases.
      </p>

      <form onSubmit={enquire} data-testid="redeem-lookup-form" className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <Label htmlFor="pin">Voucher PIN</Label>
        <div className="mt-2 flex gap-3">
          <Input data-testid="redeem-pin-input" id="pin" placeholder="1234-5678-9012-3456" value={pin} onChange={(e) => setPin(e.target.value)} className="font-mono tracking-wider" />
          <Button data-testid="redeem-lookup-btn" type="submit" disabled={busy} className="bg-[#0A2540] hover:bg-[#133D6B] text-white"><Search size={16} className="mr-2" />Look up</Button>
        </div>
      </form>

      {info && (
        <div data-testid="voucher-info-card" className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Voucher</p>
            <p className="font-mono mt-2 text-[#0A2540]">{info.pin_masked}</p>
            <p className="font-display mt-5 text-4xl font-bold text-[#0A2540]">{fmtZAR(info.balance)}</p>
            <p className="text-xs text-[#666666]">of {fmtZAR(info.original_amount)} original · {info.status.replace("_", " ")}</p>
            <p className="text-xs text-[#666666] mt-1">Expires {fmtSASTDate(info.expires_at)}</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4">
            <p className="font-display text-lg font-semibold text-[#0A2540]">Redeem amount</p>
            <div className="space-y-2"><Label>Service</Label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="redeem-service-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICES.map((s) => <SelectItem key={s.name} value={s.name}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label htmlFor="amt">Amount (ZAR)</Label><Input data-testid="redeem-amount-input" id="amt" type="number" min="0.01" step="0.01" max={info.balance} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <Button data-testid="redeem-submit-btn" onClick={redeem} disabled={busy || !amount || parseFloat(amount) <= 0 || info.status === "fully_redeemed" || info.status === "expired" || info.status === "cancelled"} className="w-full bg-[#00C4B4] hover:bg-[#009E91] text-white">
              <ScanLine size={16} className="mr-2" />Redeem now
            </Button>
          </div>
        </div>
      )}

      {receipt && (
        <div className="mt-8" data-testid="receipt-section">
          <div className="flex justify-end mb-3 no-print">
            <Button data-testid="receipt-print-btn" onClick={() => window.print()} variant="outline" className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white"><Printer size={16} className="mr-2" />Print receipt</Button>
          </div>
          <Receipt data={receipt} merchantName="FlexiVouch Merchant" />
        </div>
      )}
    </DashboardShell>
  );
}
